"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.metadataSchema = exports.metadataQuerySchema = exports.myAccessSchema = exports.APP_ENVS = exports.groupGrantSchema = exports.practiceGrantSchema = void 0;
// Wire shapes of the ACO module's access routes (SPEC §9 `/api/me` and `/api/meta`, CR-001 §5.3 and
// §8; J-034). `GET /api/v1/me/access` is the caller's own module access; `GET /api/v1/metadata` is
// what the shell needs, scoped to the caller's ACO set or, for a practice role, its grants' ACOs.
// Numbers are raw (SPEC §9: "All responses carry numbers unformatted"). No field carries PHI.
const zod_1 = require("zod");
/** One practice grant: an (ACO, TIN) pair from `core.user_practice_access` (CR-001 §6.6). */
exports.practiceGrantSchema = zod_1.z
    .object({ acoCode: zod_1.z.string(), tin: zod_1.z.string() })
    .meta({ id: "AcoPracticeGrant" });
/** One performance-group grant from `core.user_group_access` (J-238): the group's ACO, id, name and year. */
exports.groupGrantSchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    groupId: zod_1.z.number().int(),
    groupName: zod_1.z.string(),
    performanceYear: zod_1.z.number().int(),
    /**
     * J-278 (QB3 item 11a): whether the group view may show the group's combined figures. True for a
     * group user's grants and a practice user's granted groups (every practice of the group is in its
     * scope); false for a group a practice user sees only through a practice granted alone.
     */
    combinedFiguresAllowed: zod_1.z.boolean(),
})
    .meta({ id: "AcoGroupGrant" });
/** `APP_ENV` of the running server (D-979): the one image serves QA and production, so the web asks (CR-005, J-191). */
exports.APP_ENVS = ["development", "test", "qa", "prod"];
exports.myAccessSchema = zod_1.z
    .object({
    role: zod_1.z.string(),
    /** The role's ACO module actions by statement, e.g. `{ acoQuality: ["read"] }`. */
    permissions: zod_1.z.record(zod_1.z.string(), zod_1.z.array(zod_1.z.string())),
    phiAccess: zod_1.z.boolean(),
    /**
     * J-383: the caller is offered the Gap Closure Workbook; off, the screen draws no button and both
     * `/gap-closure-workbooks` routes answer 403 `gap_closure_workbook_off`.
     */
    gapClosureWorkbook: zod_1.z.boolean(),
    allAcos: zod_1.z.boolean(),
    /** ACO codes in scope: the ACO set, or for a practice role the ACOs of its grants. */
    acos: zod_1.z.array(zod_1.z.string()),
    /**
     * Practice grants; empty for every role but `practice_manager` and `practice_staff`. J-278: every
     * practice in scope, granted alone or through a granted performance group.
     */
    practices: zod_1.z.array(exports.practiceGrantSchema),
    /**
     * J-238 (group access, the order of 2026-10-16 02:59Z item 3): how the caller is scoped. `group`: a
     * non-practice user with at least one `core.user_group_access` grant sees only those groups' rows;
     * their ACO set and all_acos are ignored (it only narrows).
     */
    scopeKind: zod_1.z.enum(["aco-set", "practice", "group"]),
    /**
     * The granted performance groups, in display order; empty for `aco-set`. J-278: for `practice`, the
     * groups the user sees, granted or of a practice in scope, with `combinedFiguresAllowed`.
     */
    groups: zod_1.z.array(exports.groupGrantSchema),
    /** `SESSION_IDLE_MINUTES` (D-989): the client idle timer signs out after this many minutes. */
    idleTimeoutMinutes: zod_1.z.number().int(),
    /**
     * `APP_ENV` of the running server. Every signed-in user reads it here, so a user whom metadata refuses
     * still never sees what production does not have (J-209, inbox 06 PR D item 9).
     */
    appEnv: zod_1.z.enum(exports.APP_ENVS),
})
    .meta({ id: "AcoMyAccess" });
// No `.meta({ id })` on a querystring schema: @fastify/swagger expands parameters inline.
exports.metadataQuerySchema = zod_1.z.object({
    year: zod_1.z.coerce.number().int().min(2000).max(2100).optional(),
});
const metadataAcoSchema = zod_1.z.object({
    acoCode: zod_1.z.string(),
    acoName: zod_1.z.string(),
    /** The ACO has calculated results (for the year asked, or any year): screens open on the first such ACO (CR-005 item 3, J-191). */
    hasData: zod_1.z.boolean(),
    /** The years with calculated results, newest first: a screen's default year (J-209, inbox 06 PR D item 12). */
    dataYears: zod_1.z.array(zod_1.z.number().int()),
});
const metadataBatchSchema = zod_1.z.object({
    batchId: zod_1.z.number().int(),
    sourceType: zod_1.z.string(),
    performanceYear: zod_1.z.number().int().nullable(),
    isSynthetic: zod_1.z.boolean(),
    status: zod_1.z.string(),
    loadedAt: zod_1.z.string(),
    rowsLoaded: zod_1.z.number().int(),
});
const scoringParameterSchema = zod_1.z.object({
    year: zod_1.z.number().int(),
    name: zod_1.z.string(),
    valueNumeric: zod_1.z.number().nullable(),
    valueText: zod_1.z.string().nullable(),
});
exports.metadataSchema = zod_1.z
    .object({
    /** `APP_ENV`: `prod` hides the demonstration and legacy controls (CR-005 items 2, 4, 5; J-191). */
    appEnv: zod_1.z.enum(exports.APP_ENVS),
    /** Performance years with configuration, newest first. */
    years: zod_1.z.array(zod_1.z.number().int()),
    acos: zod_1.z.array(metadataAcoSchema),
    batches: zod_1.z.array(metadataBatchSchema),
    /** Any live batch is synthetic: the shell shows the demonstration banner (SPEC §7.0). */
    hasSyntheticData: zod_1.z.boolean(),
    /** The banner's words, `banner_text` of the stored synthetic document; null when none is stored (J-059). */
    syntheticBannerText: zod_1.z.string().nullable(),
    /** A settings change marked the results stale: the shell offers Recalculate (SPEC §14.1, J-059). */
    calculationStale: zod_1.z.boolean(),
    scoringParameters: zod_1.z.array(scoringParameterSchema),
})
    .meta({ id: "AcoMetadata" });
//# sourceMappingURL=access.js.map